import _ from 'lodash'
import { PLAYER_ARRANGEMENT_OPTIONS, PLAYER_ARRANGEMENTS } from './player-setup-arrangement/player-setup-arrangement'

const convertCustomToStandardResolution = (custom) => {
  const extractedCustomResolution = /^(?<pWidth>\d{4}) (?<pHeight>\d{4}) (?<theRest>.*)$/.exec(custom)

  return extractedCustomResolution?.groups ? `${extractedCustomResolution.groups.pWidth}x${extractedCustomResolution.groups.pHeight}` : null
}

export const convertStandardToObjectResolution = (resolution) => {
  const extractedStandardResolution = /^(?<pWidth>\d{4})x(?<pHeight>\d{4})$/.exec(resolution)

  return extractedStandardResolution?.groups
    ? {
        width: Number(extractedStandardResolution.groups.pWidth),
        height: Number(extractedStandardResolution.groups.pHeight)
      }
    : null
}

const REFRESH_DEFAULT = 60

export class Player {
  data = null
  updatedConfigs = []
  applicationType = null
  output = 1
  arrangement = ''
  canvasData = { width: 0, height: 0 }

  constructor (data) {
    this.data = data
  }

  hasData () {
    return this.data !== null
  }

  hasStandardDisplayVideoMode () {
    return this.getDisplayVideoMode() !== undefined
  }

  getApplicationType () {
    return this.applicationType
  }

  getArrangement () {
    return this.arrangement
  }

  getConfig () {
    if (!this.data?.config) {
      return {}
    }

    return this.data.config
  }

  getData () {
    return this.data
  }

  getTags () {
    return this.data?.tags ?? []
  }

  getConfigRequestData () {
    let requestConfig = {}

    this.updatedConfigs.forEach((item) => {
      requestConfig = {
        ...requestConfig,
        [item]: this.data.config[item]
      }
    })

    return {
      config: requestConfig,
      name: this.data.name
    }
  }

  getDeviceType () {
    return this.data?.deviceType
  }

  getDisplayCustomVideoMode () {
    const config = this.getConfig()

    return config['display-custom-video-mode']
  }

  getDisplayVideoMode () {
    const config = this.getConfig()

    return config['display-video-mode'] ?? []
  }

  getLicenses () {
    return this.data?.licenses
  }

  getName () {
    return this.data?.name
  }

  getOrientation () {
    const config = this.getConfig()

    return config['display-orientation']
  }

  getOutputCount () {
    if (this.data?.outputOptions?.layouts && this.data.outputOptions.layouts.length) {
      return this.data?.outputOptions?.count ?? 1
    }

    return 1
  }

  getAvailableOutputs () {
    if (this.data?.outputOptions?.layouts && this.data.outputOptions.layouts.length) {
      return this.data?.outputOptions?.outputs ?? []
    }

    return 1
  }

  /**
   * Get particula routput from the list of available outputs
   * @param {Number} index - index of output to get
   * @returns output or null
   */
  getAvailableOutput (index) {
    return this.data?.outputOptions?.outputs[index] ?? null
  }

  // For getting resolution in general without knowing the type of displayvideomode in config
  getResolution () {
    // For standard resolution
    if (this.hasStandardDisplayVideoMode()) {
      const displayVideoMode = this.getDisplayVideoMode()
      // If array
      if (Array.isArray(displayVideoMode)) {
        return this.getMultipleResolutions()
      }

      // If single object
      if (typeof displayVideoMode === 'object' && !Array.isArray(displayVideoMode)) {
        return this.getSingleDisplayResolution()
      }
    } else {
      // For custom resolution
      const displayCustomVideoMode = this.getDisplayCustomVideoMode()

      if (displayCustomVideoMode) {
        return convertCustomToStandardResolution(displayCustomVideoMode)
      }
    }

    return null
  }

  // Specific for single display resolutions (either standard or custom)
  getSingleDisplayResolution () {
    const displayVideoMode = this.getDisplayVideoMode()
    if (displayVideoMode?.resolution) {
      return displayVideoMode.resolution ?? null
    }

    if (displayVideoMode?.custom) {
      return convertCustomToStandardResolution(displayVideoMode.custom)
    }

    return null
  }

  // Specific for multiple display resolutions
  getMultipleResolutions () {
    const resolutions = this.getDisplayVideoMode().reduce((filteredResolutions, videomode) => {
      if (videomode && videomode.resolution) {
        filteredResolutions.push(videomode.resolution)
      }

      return filteredResolutions
    }, [])

    // get initial if multiple
    return resolutions[0] ?? null
  }

  // Specific for multiple display resolutions
  getConfiguredDisplayVideoModes () {
    return this.getDisplayVideoMode().filter(mode => mode && mode.resolution)
  }

  getSerialNumber () {
    return this.data?.serialNumber
  }

  getTimezone () {
    const config = this.getConfig()

    return config.timezone
  }

  getResolutions () {
    return this.data?.outputOptions?.resolutions
  }

  getOutput () {
    return this.output
  }

  getCanvas () {
    return this.data?.config.canvas ?? null
  }

  getCanvasAsObject () {
    // NOTE: canvas dimensions are always stays the same regardless rotation, so we need to
    // switch width/height to make it consumable for the downstream components
    if (this.getOrientation() && this.getOrientation() !== 'horizontal' && this.canvasData) {
      return { width: this.canvasData.height, height: this.canvasData.width }
    }
    return this.canvasData ?? { width: 0, height: 0 }
  }

  getVideoOutputSelector () {
    return this.data['video-output-selector']
  }

  setEnableArya () {
    if (!this.data.config) {
      this.data.config = {}
    }

    this.data.config['enable-arya'] = null
    this.updatedConfigs.push('enable-arya')
  }

  setCanvas (width, height) {
    if (!this.data.config) {
      this.data.config = {}
    }

    this.data.config.canvas = `${width} ${height}`
    this.canvasData = {
      width,
      height
    }

    this.updatedConfigs.push('canvas')
  }

  resetCanvas () {
    if (!this.data.config) {
      this.data.config = {}
    }

    this.data.config.canvas = null

    this.updatedConfigs.push('canvas')
  }

  getOutputOptions () {
    return this.data?.outputOptions?.outputs
  }

  setOutput (output) {
    this.output = output
  }

  setApplicationType (applicationType) {
    this.applicationType = applicationType
  }

  setArrangement (arrangement) {
    this.arrangement = arrangement
  }

  setDisplayVideoMode (displayVideoMode) {
    const config = this.getConfig()

    this.data.config = {
      ...config,
      'display-video-mode': displayVideoMode
    }
    this.updatedConfigs.push('display-video-mode')
  }

  setName (name) {
    this.data.name = name
  }

  setOrientation (orientation) {
    const config = this.getConfig()

    this.data.config = {
      ...config,
      'display-orientation': orientation
    }
    this.updatedConfigs.push('display-orientation')
  }

  setTimezone (timezone) {
    const config = this.getConfig()

    this.data.config = {
      ...config,
      timezone
    }
    this.updatedConfigs.push('timezone')
  }

  /**
   * Abstract function to generate offsets for any combination of displays arrangement
   * @param {Number} columns - number of horizontal displays
   * @param {Number} rows - number of vertical displays
   * @param {Array} outputs - repository with [{ outputId: 'OUTPUT-HDMI-ID-1' }] records
   * @param {Number} resolutionHorizontal - horizontal resoultion (display width in pixels)
   * @param {Number} resolutionVertical - vertical resolution (display height in pixels)
   * @param {Number} bezelHorizontal - bezel horizontal in pixels (between displays from the top to bottom)
   * @param {Number} bezelVertical - bezel vertical in pixels  (between displays from the left to right)
   * @returns offsets generated both for vertical and horizontal parameters e.g. [{ x: X, y: Y, outputId: 'OUTPUT-ID-1' }]
   */
  calculateOffsets (columns, rows, outputs, resolutionHorizontal, resolutionVertical, bezelHorizontal, bezelVertical) {
    let outputsCounter = 0
    let offsets = []

    let xOffset = 0
    let yOffset = 0

    for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
      // Skip yOffset calculation for first row, it's basically just (0,0) initially
      // And for arrangements that only have 1 row, since PositionY basically does not change
      if (rowIndex > 0) {
        // For next rows, calculate PositionY of output by getting the PositionY(N-1) + Height(N-1) + Bezel
        const prevYOffset = offsets[offsets.length - 1].y
        yOffset = prevYOffset + resolutionVertical + Math.round(bezelHorizontal)

        outputsCounter += 1
      }

      // Recording starts from each row first, by traversing all columns
      // Record initial column of a row
      const rowRecords = [{ x: xOffset, y: yOffset, output: outputs[outputsCounter].outputId }]

      // Then travese succeeding columns
      for (let colIndex = 0; colIndex < columns - 1; colIndex++) {
        // For next columns, same with rows, calculate PositionX of output by getting PositionX(N-1) + Width(N-1) + Bezel
        const prevXOffset = rowRecords[colIndex].x
        xOffset = prevXOffset + resolutionHorizontal + Math.round(bezelVertical)

        outputsCounter += 1

        rowRecords.push({
          x: xOffset,
          y: yOffset,
          output: outputs[outputsCounter].outputId
        })
      }

      // Reset xOffset to 0 for every new row
      xOffset = 0
      offsets = offsets.concat(rowRecords)
    }

    return offsets
  }

  /**
   * Set Vertical and Horizontal bezels for a screen. Expected values in pixels.
   * Consider number of both player configuration and other parameters:
   *  - number of outputs to configure
   *  - list of available outputs
   *  - displays resolution (expected to be the same for all displays)
   *  - arrangement (vertical or horizontal)
   *  - display orientation (rorate left/right or horizontal)
   *
   *  Sets two configuraiton attributes:
   *  - `video-output-selector` with appropriate offsets
   *  - `canvas` with full dimensions of the media expected
   *
   * @param {*} verticalOffset - vertical bezel in pixels:
   *  [D1 |vertical bezel| D2]
   *  [D3 |vertical bezel| D4]
   * @param {*} horizontalOffset - horizontal bezel in pixels
   *  [D1            D2]
   *  |horizontal bezel|
   *  [D3            D4]
   */
  setVideoWallOutputOffsets (verticalBezel, horizontalBezel) {
    const outputs = this.getAvailableOutputs()
    const outputsQty = this.getOutput()
    const resolution = convertStandardToObjectResolution(this.getResolution())
    const arrangement = PLAYER_ARRANGEMENT_OPTIONS[this.getArrangement()]?.arrangement ?? PLAYER_ARRANGEMENTS.x2x2

    if (!resolution) {
      throw new Error('Display resolution is invalid, cannot set offset')
    }

    let displayWidth = resolution.width
    let displayHeight = resolution.height

    if (this.getOrientation() !== 'horizontal') {
      displayHeight = resolution.width
      displayWidth = resolution.height
    }

    let canvasWidth = 0
    let canvasHeight = 0

    let offsets = null

    const outputsPerRowQty = Math.sqrt(outputsQty)
    const orientation = this.getOrientation()

    switch (`${arrangement} ${orientation}`) {
      case `${PLAYER_ARRANGEMENTS.horizontal} horizontal`:
        offsets = this.calculateOffsets(
          outputsQty,
          1,
          outputs,
          displayWidth,
          displayHeight,
          0,
          verticalBezel)
        canvasHeight = displayHeight
        canvasWidth = Math.max(...offsets.map(offset => offset.x)) + displayWidth
        break
      case `${PLAYER_ARRANGEMENTS.vertical} horizontal`:
        offsets = this.calculateOffsets(
          1,
          outputsQty,
          outputs,
          displayWidth,
          displayHeight,
          horizontalBezel,
          0)
        canvasWidth = displayWidth
        canvasHeight = Math.max(...offsets.map(offset => offset.y)) + displayHeight
        break
      case `${PLAYER_ARRANGEMENTS.horizontal} rotateLeft`:
      case `${PLAYER_ARRANGEMENTS.horizontal} rotateRight`:
        offsets = this.calculateOffsets(
          1,
          outputsQty,
          outputs,
          displayHeight,
          displayWidth,
          verticalBezel,
          0)
        canvasWidth = displayHeight
        canvasHeight = Math.max(...offsets.map(offset => offset.y)) + displayWidth
        break
      case `${PLAYER_ARRANGEMENTS.vertical} rotateLeft`:
      case `${PLAYER_ARRANGEMENTS.vertical} rotateRight`:
        offsets = this.calculateOffsets(
          outputsQty,
          1,
          outputs,
          displayHeight,
          displayWidth,
          0,
          horizontalBezel)

        canvasWidth = Math.max(...offsets.map(offset => offset.x)) + displayHeight
        canvasHeight = displayWidth
        break
      case `${PLAYER_ARRANGEMENTS.x2x2} rotateRight`:
      case `${PLAYER_ARRANGEMENTS.x2x2} rotateLeft`:
        offsets = this.calculateOffsets(
          outputsPerRowQty,
          outputsPerRowQty,
          outputs,
          displayHeight,
          displayWidth,
          horizontalBezel,
          verticalBezel)

        canvasWidth = Math.max(...offsets.map(offset => offset.x)) + displayHeight
        canvasHeight = Math.max(...offsets.map(offset => offset.y)) + displayWidth
        break
      case `${PLAYER_ARRANGEMENTS.x2x2} horizontal`:
        offsets = this.calculateOffsets(
          outputsPerRowQty,
          outputsPerRowQty,
          outputs,
          displayWidth,
          displayHeight,
          horizontalBezel,
          verticalBezel)

        canvasWidth = Math.max(...offsets.map(offset => offset.x)) + displayWidth
        canvasHeight = Math.max(...offsets.map(offset => offset.y)) + displayHeight
        break
    }

    // set canvas, then video-output-selector, b/c both works together
    this.setCanvas(canvasWidth, canvasHeight)
    this.setVideoOutputSelector(offsets.map(({ output, x, y }) => `${output}:${x}:${y}`).join(','))

    return offsets
  }

  setBezelPositions (offsets, verticalBezel, horizontalBezel) {
    const arrangement = PLAYER_ARRANGEMENT_OPTIONS[this.getArrangement()]?.arrangement ?? PLAYER_ARRANGEMENTS.x2x2
    const resolution = convertStandardToObjectResolution(this.getResolution())
    const bezelInfo = []

    if (this.getOrientation() !== PLAYER_ARRANGEMENTS.horizontal) {
      const resHeight = resolution.height
      resolution.height = resolution.width
      resolution.width = resHeight
    }

    offsets.forEach((output, index) => {
      if (arrangement === PLAYER_ARRANGEMENTS.vertical) {
        const bezelWidth = resolution.width
        const bezelHeight = Math.round(horizontalBezel)

        if (index === 0) {
          bezelInfo.push({
            x: 0,
            y: resolution.height,
            width: bezelWidth,
            height: bezelHeight
          })
        } else {
          if (index < offsets.length - 1) {
            bezelInfo.push({
              x: 0,
              y: resolution.height * (index + 1) + bezelHeight * index,
              width: bezelWidth,
              height: bezelHeight
            })
          }
        }
      } else if (arrangement === PLAYER_ARRANGEMENTS.horizontal) {
        const bezelWidth = Math.round(verticalBezel)
        const bezelHeight = resolution.height // make sure swap for width, height has been handled here based on orientation

        if (index === 0) {
          bezelInfo.push({
            x: resolution.width,
            y: 0,
            width: bezelWidth,
            height: bezelHeight
          })
        } else {
          if (index < offsets.length - 1) {
            bezelInfo.push({
              x: (resolution.width * (index + 1)) + (bezelWidth * index),
              y: 0,
              width: bezelWidth,
              height: bezelHeight
            })
          }
        }
      }
    })

    if (arrangement === PLAYER_ARRANGEMENTS.x2x2) {
      const vBezelWidth = Math.round(verticalBezel)
      const vBezelHeight = resolution.height
      const hBezelWidth = resolution.width
      const hBezelHeight = Math.round(horizontalBezel)

      // vertical bezel #1 (right)
      bezelInfo.push({
        x: resolution.width,
        y: 0,
        width: vBezelWidth,
        height: vBezelHeight
      })

      // horizontal bezel #1 (bottom)
      bezelInfo.push({
        x: 0,
        y: resolution.height,
        width: hBezelWidth,
        height: hBezelHeight
      })

      // horizontal bezel #2 (bottom-right)
      bezelInfo.push({
        x: resolution.width + vBezelWidth,
        y: resolution.height,
        width: hBezelWidth,
        height: hBezelHeight
      })

      // vertical bezel #2 (right-bottom)
      bezelInfo.push({
        x: resolution.width,
        y: resolution.height + hBezelHeight,
        width: vBezelWidth,
        height: vBezelHeight
      })

      // the intersection
      bezelInfo.push({
        x: resolution.width,
        y: resolution.height,
        width: vBezelWidth,
        height: hBezelHeight
      })
    }

    return bezelInfo
  }

  setVideoOutputSelector (videoOutputConfig) {
    const config = this.getConfig()

    this.data.config = {
      ...config,
      'video-output-selector': videoOutputConfig
    }

    this.updatedConfigs.push('video-output-selector')
  }
}

export class PlayerBuilder {
  player

  constructor () {
    this.player = new Player(null)
  }

  getPlayer () {
    return this.player
  }

  getDefaultResolutions () {
    return [
      { label: '4k', width: 3840, height: 2160 },
      { label: '1080p', width: 1920, height: 1080 }
    ]
  }

  reset (defaultData) {
    this.player = new Player(defaultData)
  }

  setApplicationType (applicationType) {
    // cleanup video output selector if it is single-output application
    if (applicationType === 'application-single' && this.player.getVideoOutputSelector() !== null) {
      this.player.setVideoOutputSelector('')
    }

    this.player.setApplicationType(applicationType)
  }

  setArrangement (arrangement) {
    this.player.setArrangement(arrangement)
  }

  setName (name) {
    this.player.setName(name)
  }

  setOrientation (orientation) {
    if (['horizontal', 'rotateLeft', 'rotateRight', 'flipVert'].indexOf(orientation) < 0) {
      throw new Error(`Couldn't recognize orientation: ${orientation}`)
    }

    this.player.setOrientation(orientation)
  }

  getPixelSize (diagonal) {
    const { width, height } = convertStandardToObjectResolution(this.player.getResolution())
    const diagonalPixels = Math.sqrt(width ** 2 + height ** 2)
    return diagonalPixels / diagonal
  }

  /**
   * Specify VideoWall bezel. It is very important to use same units for both bezels and diagonal.
   * @param {*} diagonal - vertical bezel in mm or inches
   * @param {*} verticalBezel - vertical bezel in mm or inches
   *  [D1 |vertical bezel| D2]
   *  [D3 |vertical bezel| D4]
   * @param {*} horizontalBezel - horizontal bezel in mm or inches
   *  [D1            D2]
   *  |horizontal bezel|
   *  [D3            D4]
   */
  setBezels (diagonal, verticalBezel, horizontalBezel) {
    const pixelSize = this.getPixelSize(diagonal)

    const offsets = this.getPlayer().setVideoWallOutputOffsets(
      verticalBezel * pixelSize,
      horizontalBezel * pixelSize)

    return this.getPlayer().setBezelPositions(offsets, verticalBezel * pixelSize, horizontalBezel * pixelSize)
  }

  setResolution (resolution) {
    const displayVideoMode = {
      'restrict-type': 'hdmi',
      'force-std-mode': true,
      'vertical-freq': REFRESH_DEFAULT,
      resolution
    }

    const newDisplayVideoMode = [
      null, // For resetting any previously set display video mode config on the player
      displayVideoMode
    ]

    this.player.setDisplayVideoMode(newDisplayVideoMode)
  }

  enableArya () {
    this.getPlayer().setEnableArya()
  }

  setOutput (output) {
    this.player.setOutput(output)
  }

  setTimezone (timezone) {
    this.player.setTimezone(timezone)
  }

  /**
   * This is important to reset offsets in (video-output-selector)
   * for case of the Player re-configuration.
   */
  configureSingleOutput () {
    const resolution = this.getPlayer().getResolution()

    if (!resolution || !/\d[x]\d/.test(resolution)) {
      throw new Error('Cannot configure single output without screen resolution')
    }

    // Reset canvas if multi-output, otherwise, skip setting canvas in config
    if (this.player.getOutputCount() > 1) {
      this.getPlayer().resetCanvas()
    }

    // NOTE: we can't use getAvailableOutputs for a single-output players
    const output = this.getPlayer().getAvailableOutput(0)
    if (output) {
      this.getPlayer().setVideoOutputSelector(`${output.outputId}:0:0`)
    }
  }

  /**
   * Separate function to force Mirroring application setup
   * to cover re-configuration player.
   */
  configureMirroring () {
    const resolution = this.getPlayer().getResolution()

    if (!resolution || !/\d[x]\d/.test(resolution)) {
      throw new Error('Cannot configure mirroring without screen resolution')
    }

    const [width, height] = resolution.split('x')
    const outputs = this.getPlayer().getAvailableOutputs()
    const outputsQty = this.getPlayer().getOutputCount()

    const offsets = []
    for (let i = 0; i < outputsQty; i++) {
      offsets.push({
        output: outputs[i].outputId,
        x: 0,
        y: 0
      })
    }

    this.getPlayer().setCanvas(Number(width), Number(height))
    this.getPlayer().setVideoOutputSelector(offsets.map((item) => {
      return `${item.output}:${item.x}:${item.y}`
    }).join(','))
  }

  setOutputPositions (tableData) {
    const outputOptions = this.player.getOutputOptions()
    const arrangement = PLAYER_ARRANGEMENT_OPTIONS[this.player.getArrangement()]?.arrangement ?? PLAYER_ARRANGEMENTS.x2x2
    const outputsPerRowQty = Math.sqrt(tableData.length)

    let outputs = []

    const calculatePositions = (columns, rows, outputs) => {
      let outputsCounter = 0
      let outputsPositions = []

      let xPos = 0
      let yPos = 0

      for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
        // Skip yPos calculation for first row, it's basically just (0,0) initially
        // And for arrangements that only have 1 row, since PositionY basically does not change
        if (rowIndex > 0) {
          // For next rows, calculate PositionY of output by getting the PositionY(N-1) + Height(N-1)
          const prevyPos = outputsPositions[outputsPositions.length - 1].y
          yPos = prevyPos + outputs[outputsCounter].height

          outputsCounter += 1
        }

        const rowOutputId = outputOptions[outputsCounter].outputId

        // Recording starts from each row first, by traversing all columns
        // Record initial column of a row
        const rowRecords = [{ x: xPos, y: yPos, output: rowOutputId }]

        // Then travese succeeding columns
        for (let colIndex = 0; colIndex < columns - 1; colIndex++) {
          // For next columns, same with rows, calculate PositionX of output by getting PositionX(N-1) + Width(N-1)
          const prevxPos = rowRecords[colIndex].x
          xPos = prevxPos + outputs[outputsCounter].width

          outputsCounter += 1
          const colOutputId = outputOptions[outputsCounter].outputId

          rowRecords.push({
            x: xPos,
            y: yPos,
            output: colOutputId
          })
        }

        // Reset xPos to 0 for every new row
        xPos = 0
        outputsPositions = outputsPositions.concat(rowRecords)
      }
      return outputsPositions
    }

    const calculateCanvasSize = (outputs) => {
      return _.chain(outputs)
        .reduce((prev, cur) => {
          return { width: prev.width + cur.width, height: prev.height + cur.height }
        }, { width: 0, height: 0 })
        .value()
    }

    const { width: canvasWidth, height: canvasHeight } = calculateCanvasSize(tableData)

    switch (`${arrangement} ${this.player.getOrientation()}`) {
      case `${PLAYER_ARRANGEMENTS.horizontal} horizontal`:
        outputs = calculatePositions(
          tableData.length,
          1,
          tableData
        )
        this.player.setCanvas(_.chain(tableData).map('width').sum(), tableData[0].height)
        break
      case `${PLAYER_ARRANGEMENTS.horizontal} rotateLeft`:
      case `${PLAYER_ARRANGEMENTS.horizontal} rotateRight`:
        outputs = calculatePositions(
          1,
          tableData.length,
          tableData.map((row) => { return { ...row, width: row.height, height: row.width } })
        )
        this.player.setCanvas(tableData[0].height, canvasWidth)
        break
      case `${PLAYER_ARRANGEMENTS.vertical} horizontal`:
        outputs = calculatePositions(
          1,
          tableData.length,
          tableData
        )
        this.player.setCanvas(tableData[0].width, _.chain(tableData).map('height').sum())
        break
      case `${PLAYER_ARRANGEMENTS.vertical} rotateLeft`:
      case `${PLAYER_ARRANGEMENTS.vertical} rotateRight`:
        outputs = calculatePositions(
          tableData.length,
          1,
          // NOTE: remap Width and Height to properly calculate offsets
          tableData.map((row) => { return { ...row, width: row.height, height: row.width } })
        )

        this.player.setCanvas(canvasHeight, tableData[0].width)
        break
      case `${PLAYER_ARRANGEMENTS.x2x2} horizontal`:
        outputs = calculatePositions(
          outputsPerRowQty,
          outputsPerRowQty,
          tableData
        )

        this.player.setCanvas(
          Math.round(_.chain(tableData).map('width').sum().value() / 2),
          Math.round(_.chain(tableData).map('height').sum().value() / 2)
        )
        break
      case `${PLAYER_ARRANGEMENTS.x2x2} rotateLeft`:
      case `${PLAYER_ARRANGEMENTS.x2x2} rotateRight`:
        outputs = calculatePositions(
          outputsPerRowQty,
          outputsPerRowQty,
          tableData.map((row) => { return { ...row, width: row.height, height: row.width } })
        )
        // 2x2, so both aggregated height and width divide by 2
        this.player.setCanvas(
          Math.round(_.chain(tableData).map('height').sum().value() / 2),
          Math.round(_.chain(tableData).map('width').sum().value() / 2)
        )
        break
    }

    this.player.setVideoOutputSelector(outputs.map((item) => {
      return `${item.output}:${item.x}:${item.y}`
    }).join(','))
  }
}
